import { useState } from 'react';
import Swal from 'sweetalert2';
import type { FeatureFlag } from '@/mocks/saFeatureFlags';
import { flagCategoryMeta } from '@/mocks/saFeatureFlags';

interface FeatureFlagDetailModalProps {
  flag: FeatureFlag;
  onClose: () => void;
}

export default function FeatureFlagDetailModal({ flag, onClose }: FeatureFlagDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'deps' | 'docs'>('info');
  const cat = flagCategoryMeta[flag.category];

  const handleRollback = (entryId: number) => {
    Swal.fire({
      icon: 'warning',
      title: '¿Confirmar Rollback?',
      text: `Estás por revertir a un estado anterior del feature flag "${flag.name}". Esta acción se registrará en el historial.`,
      showCancelButton: true,
      confirmButtonText: 'Ejecutar Rollback',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'success',
          title: 'Rollback ejecutado',
          text: `El feature flag "${flag.name}" fue revertido exitosamente.`,
          timer: 2500,
          showConfirmButton: true,
          confirmButtonColor: '#1a1a1a',
          customClass: {
            popup: 'rounded-2xl text-sm',
            title: 'text-sm font-bold',
            confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
          },
        });
      }
    });
  };

  const tabs = [
    { id: 'info' as const, label: 'Información', icon: 'ri-information-line' },
    { id: 'history' as const, label: 'Historial', icon: 'ri-history-line', count: flag.history.length },
    { id: 'deps' as const, label: 'Dependencias', icon: 'ri-link', count: flag.dependencies.length + flag.modules.length },
    { id: 'docs' as const, label: 'Documentación', icon: 'ri-book-open-line' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl border border-background-200 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl ${cat.bgColor} flex items-center justify-center shrink-0`}>
              <i className={`${cat.icon} ${cat.color} text-base`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-foreground-950 truncate">{flag.name}</h3>
              <p className="text-foreground-400 text-[10px]">{flag.key}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors shrink-0">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 border-b border-background-200 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-[1px] ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-foreground-400 hover:text-foreground-600'
              }`}
            >
              <i className={`${tab.icon} text-[11px]`}></i>
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-bold">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'info' && (
            <div className="space-y-4">
              {/* Description */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Descripción</p>
                <p className="text-foreground-700 text-xs leading-relaxed">{flag.description}</p>
              </div>

              {/* Environments */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Entornos</p>
                <div className="grid grid-cols-3 gap-2">
                  {(['development', 'qa', 'production'] as const).map((env) => {
                    const active = flag.environments[env];
                    const labels: Record<string, string> = { development: 'Development', qa: 'QA', production: 'Production' };
                    const icons: Record<string, string> = { development: 'ri-code-line', qa: 'ri-test-tube-line', production: 'ri-global-line' };
                    return (
                      <div key={env} className={`rounded-xl p-3 border ${active ? 'border-emerald-200 bg-emerald-50' : 'border-background-200 bg-background-50'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2.5 h-2.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-background-300'}`}></div>
                          <i className={`${icons[env]} text-sm ${active ? 'text-emerald-600' : 'text-foreground-400'}`}></i>
                          <span className={`text-[10px] font-bold ${active ? 'text-emerald-700' : 'text-foreground-400'}`}>{labels[env]}</span>
                        </div>
                        <span className={`text-[10px] font-medium ${active ? 'text-emerald-600' : 'text-foreground-400'}`}>
                          {active ? 'Activado' : 'Desactivado'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Metadata */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Metadatos</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                    <span className="text-foreground-400 text-[9px] block mb-0.5">Versión mínima</span>
                    <span className="text-foreground-800 text-[11px] font-bold">v{flag.minVersionRequired}</span>
                  </div>
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                    <span className="text-foreground-400 text-[9px] block mb-0.5">Creado</span>
                    <span className="text-foreground-800 text-[11px] font-bold">{flag.createdAt}</span>
                  </div>
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                    <span className="text-foreground-400 text-[9px] block mb-0.5">Última modificación</span>
                    <span className="text-foreground-800 text-[11px] font-bold">{flag.updatedAt}</span>
                  </div>
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                    <span className="text-foreground-400 text-[9px] block mb-0.5">Modificado por</span>
                    <span className="text-foreground-800 text-[11px] font-bold">{flag.updatedBy}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              {flag.history.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-history-line text-4xl text-foreground-300 block mb-2"></i>
                  <p className="text-foreground-400 text-xs">Sin historial registrado</p>
                </div>
              ) : (
                flag.history.map((entry) => (
                  <div key={entry.id} className="bg-background-50 border border-background-200 rounded-xl p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                          entry.action === 'created' ? 'bg-sky-100 text-sky-700' :
                          entry.action === 'enabled' ? 'bg-emerald-100 text-emerald-700' :
                          entry.action === 'disabled' ? 'bg-red-100 text-red-700' :
                          entry.action === 'rolled_back' ? 'bg-amber-100 text-amber-700' :
                          'bg-background-100 text-foreground-600'
                        }`}>
                          {entry.action === 'created' ? 'Creado' :
                           entry.action === 'enabled' ? 'Activado' :
                           entry.action === 'disabled' ? 'Desactivado' :
                           entry.action === 'rolled_back' ? 'Rollback' : 'Actualizado'}
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-background-100 text-foreground-500 text-[9px] font-medium capitalize">{entry.environment}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRollback(entry.id); }}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-600 text-[9px] font-bold cursor-pointer transition-colors whitespace-nowrap"
                      >
                        <i className="ri-arrow-go-back-line text-[9px]"></i>
                        Rollback
                      </button>
                    </div>
                    <p className="text-foreground-600 text-[11px] leading-relaxed">{entry.summary}</p>
                    <div className="flex items-center gap-3 mt-2 text-[9px] text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line text-[9px]"></i>
                        {entry.changedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-[9px]"></i>
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'deps' && (
            <div className="space-y-4">
              {/* Dependencies */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">
                  Dependencias ({flag.dependencies.length})
                </p>
                {flag.dependencies.length === 0 ? (
                  <p className="text-foreground-400 text-[11px] bg-background-50 border border-background-200 rounded-xl p-3">
                    Este feature flag no tiene dependencias.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {flag.dependencies.map((dep) => (
                      <div key={dep.key} className="flex items-center justify-between bg-background-50 border border-background-200 rounded-xl p-3">
                        <div className="flex items-center gap-2.5">
                          <i className="ri-git-branch-line text-foreground-500 text-sm"></i>
                          <div>
                            <span className="text-foreground-800 text-[11px] font-semibold">{dep.name}</span>
                            <span className="text-foreground-400 text-[9px] block font-mono">{dep.key}</span>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${dep.required ? 'bg-red-100 text-red-700' : 'bg-foreground-100 text-foreground-500'}`}>
                          {dep.required ? 'Requerido' : 'Opcional'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modules */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">
                  Módulos afectados ({flag.modules.length})
                </p>
                {flag.modules.length > 3 && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200">
                    <i className="ri-alert-line text-amber-600 text-sm"></i>
                    <p className="text-amber-700 text-[10px] font-medium">
                      Este feature afecta {flag.modules.length} módulos. Verificá el impacto antes de hacer cambios.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1.5">
                  {flag.modules.map((mod) => (
                    <div key={mod.id} className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-xl p-2.5">
                      <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                        <i className={`${mod.icon} text-foreground-500 text-xs`}></i>
                      </div>
                      <span className="text-foreground-700 text-[10px] font-medium truncate">{mod.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'docs' && (
            <div>
              <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-book-open-line text-accent-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-foreground-900">Documentación rápida</h4>
                    <p className="text-foreground-400 text-[10px]">{flag.key} — v{flag.minVersionRequired}</p>
                  </div>
                </div>
                <p className="text-foreground-600 text-[11px] leading-relaxed">{flag.documentation}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-background-200 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-foreground-400 text-[9px]">Key:</span>
            <code className="px-2 py-0.5 rounded bg-background-100 text-foreground-600 text-[10px] font-mono">{flag.key}</code>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}